import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Play } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useLanguage } from "@/context/LanguageContext";

export const PodcastTeaser = () => {
  const { t, lang } = useLanguage();
  const { data: episodes = [] } = useQuery({
    queryKey: ["podcast"],
    queryFn: async () => (await api.get("/podcast")).data,
  });

  return (
    <section id="podcast" className="py-20 lg:py-32 bg-black text-white" data-testid="podcast-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-14 max-w-xl"
        >
          <p className="font-['Ubuntu'] text-xs uppercase tracking-[0.25em] font-semibold text-[#00F5D4] mb-3">{t.podcast.eyebrow}</p>
          <h2 className="font-['Cutive'] text-2xl sm:text-3xl lg:text-4xl font-bold">{t.podcast.title}</h2>
          <p className="font-['Ubuntu'] text-white/60 mt-3">{t.podcast.subtitle}</p>
        </motion.div>

        {episodes.length === 0 ? (
          <p className="font-['Ubuntu'] text-white/50" data-testid="podcast-empty">{t.podcast.empty}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {episodes.slice(0, 3).map((ep, i) => (
              <motion.div
                key={ep.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="border-2 border-white/20 rounded-2xl p-5 flex gap-4 items-center hover:border-[#00F5D4] transition-colors"
                data-testid={`podcast-card-${i}`}
              >
                <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 border-2 border-white/20">
                  {ep.cover_image && <img src={ep.cover_image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                  <h3 className="font-['Ubuntu'] font-bold text-sm">{lang === "fr" ? ep.title_fr : ep.title_pt}</h3>
                  <p className="font-['Ubuntu'] text-xs text-white/50 mt-1">{ep.duration}</p>
                </div>
                <button
                  onClick={() => toast(t.podcast.mockToast)}
                  className="w-10 h-10 rounded-full bg-[#FFE440] flex items-center justify-center shrink-0"
                  data-testid={`podcast-play-${i}`}
                >
                  <Play className="w-4 h-4 text-black fill-black" />
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
